import { motion } from "framer-motion";

/**
 * WaterFedPoleDemo — animated SVG illustration of DI water descending
 * from a water-fed pole down a pane of glass, with subtle sunlight
 * refraction and a slowly rotating building silhouette in the background.
 *
 * Pure SVG + CSS/Framer — no video, no WebGL. Loops indefinitely.
 */
export default function WaterFedPoleDemo() {
  return (
    <section
      className="relative overflow-hidden"
      style={{ background: "var(--bg-2)", padding: "120px 0" }}
      data-testid="water-fed-pole-demo"
    >
      <div className="absolute inset-0 pointer-events-none" style={{
        background: "radial-gradient(1000px 500px at 80% 20%, rgba(198,168,124,0.09), transparent 60%)",
      }} />

      <div className="max-w-[1600px] mx-auto px-6 sm:px-10 lg:px-14 relative">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.3 }}
          transition={{ duration: 1.1, ease: [0.16, 1, 0.3, 1] }}
          className="grid md:grid-cols-12 gap-8 mb-16"
        >
          <div className="md:col-span-3 overline pt-2">Water-Fed Pole · WFP</div>
          <div className="md:col-span-9">
            <h2 className="font-display text-5xl md:text-7xl" style={{ letterSpacing: "-0.035em", lineHeight: 0.95 }}>
              No ladders.{" "}
              <span className="font-display-italic" style={{ color: "var(--gold-2)" }}>No footprints.</span>
            </h2>
          </div>
        </motion.div>

        <div className="grid lg:grid-cols-12 gap-10 items-center">
          {/* SVG illustration */}
          <div className="lg:col-span-7">
            <div
              className="relative w-full"
              style={{
                aspectRatio: "16 / 10",
                borderRadius: "28px",
                border: "1px solid var(--line-gold)",
                boxShadow: "0 40px 80px -20px rgba(0,0,0,0.6)",
                background: "linear-gradient(160deg, #0a0a0d 0%, #12121a 60%, #0a0a0d 100%)",
                overflow: "hidden",
              }}
            >
              {/* Ambient warm sun glow */}
              <div className="absolute inset-0 pointer-events-none" style={{
                background: "radial-gradient(400px 200px at 78% 30%, rgba(232,217,197,0.22), transparent 70%)",
              }} />

              {/* Slowly rotating building silhouette */}
              <motion.svg
                viewBox="0 0 1000 620"
                className="absolute inset-0 w-full h-full"
                animate={{ rotateY: [-3, 3, -3] }}
                transition={{ duration: 24, ease: "easeInOut", repeat: Infinity }}
                style={{ transformStyle: "preserve-3d", transformOrigin: "center" }}
              >
                {/* Building silhouette */}
                <defs>
                  <linearGradient id="glassGrad" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="rgba(198,168,124,0.10)" />
                    <stop offset="50%" stopColor="rgba(232,217,197,0.16)" />
                    <stop offset="100%" stopColor="rgba(198,168,124,0.06)" />
                  </linearGradient>
                  <linearGradient id="wallGrad" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#1a1c22" />
                    <stop offset="100%" stopColor="#0a0a0d" />
                  </linearGradient>
                  <linearGradient id="poleGrad" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#C6A87C" />
                    <stop offset="50%" stopColor="#E8D9C5" />
                    <stop offset="100%" stopColor="#C6A87C" />
                  </linearGradient>
                  {/* Water gradient */}
                  <linearGradient id="waterGrad" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="rgba(255,255,255,0.6)" />
                    <stop offset="100%" stopColor="rgba(198,168,124,0.3)" />
                  </linearGradient>
                </defs>

                {/* Wall */}
                <rect x="180" y="60" width="640" height="540" fill="url(#wallGrad)" />

                {/* Glass panes — grid */}
                {[0, 1, 2].map((row) =>
                  [0, 1, 2, 3].map((col) => (
                    <rect
                      key={`${row}-${col}`}
                      x={210 + col * 148}
                      y={90 + row * 168}
                      width="132"
                      height="152"
                      fill="url(#glassGrad)"
                      stroke="rgba(198,168,124,0.35)"
                      strokeWidth="1"
                    />
                  ))
                )}

                {/* Subtle window reflections (sunlight streaks) */}
                {[0, 1, 2].map((row) =>
                  [0, 1, 2, 3].map((col) => (
                    <line
                      key={`ref-${row}-${col}`}
                      x1={218 + col * 148}
                      y1={100 + row * 168}
                      x2={280 + col * 148}
                      y2={140 + row * 168}
                      stroke="rgba(255,255,255,0.18)"
                      strokeWidth="1"
                    />
                  ))
                )}

                {/* Water-fed pole (extending from bottom-left to top) */}
                <motion.g
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ duration: 1.4, delay: 0.3 }}
                >
                  {/* Pole shaft */}
                  <rect x="88" y="140" width="10" height="440" fill="url(#poleGrad)" rx="4" />
                  {/* Pole head (brush + water jet) */}
                  <circle cx="93" cy="130" r="14" fill="#E8D9C5" opacity="0.9" />
                  <rect x="80" y="126" width="26" height="8" fill="#C6A87C" rx="2" />
                </motion.g>

                {/* Water flowing down each pane — staggered animation */}
                {[0, 1, 2, 3].map((col) => (
                  <motion.g key={`water-${col}`}>
                    {/* Water sheet — vertical stream */}
                    <motion.rect
                      x={214 + col * 148 + 60}
                      y={120}
                      width="14"
                      height="450"
                      fill="url(#waterGrad)"
                      opacity="0.65"
                      initial={{ scaleY: 0, transformOrigin: "top" }}
                      animate={{ scaleY: [0, 1, 1, 0], transformOrigin: "top" }}
                      transition={{ duration: 6, delay: col * 0.7, repeat: Infinity, repeatDelay: 2, ease: [0.16, 1, 0.3, 1] }}
                      style={{ filter: "blur(1px)" }}
                    />
                    {/* Water drops trickling */}
                    {[0, 1, 2].map((d) => (
                      <motion.circle
                        key={`drop-${col}-${d}`}
                        cx={220 + col * 148 + 65}
                        r={2 + (d % 2)}
                        fill="rgba(255,255,255,0.9)"
                        initial={{ cy: 130 }}
                        animate={{ cy: [130, 560], opacity: [0, 1, 0] }}
                        transition={{
                          duration: 2.5,
                          delay: col * 0.7 + d * 0.6,
                          repeat: Infinity,
                          repeatDelay: 1.5,
                          ease: "easeIn",
                        }}
                      />
                    ))}
                  </motion.g>
                ))}

                {/* Ground reflection */}
                <rect x="0" y="590" width="1000" height="30" fill="rgba(198,168,124,0.14)" />
              </motion.svg>

              {/* Overlay labels */}
              <div className="absolute top-5 left-5 font-mono text-[10px] uppercase tracking-[0.36em] px-3 py-1.5" style={{
                background: "rgba(0,0,0,0.55)",
                color: "var(--gold-2)",
                borderRadius: "999px",
                border: "1px solid var(--line-gold)",
                backdropFilter: "blur(8px)",
              }}>
                Live · DI Water · 0 TDS
              </div>
              <div className="absolute bottom-5 right-5 font-mono text-[10px] uppercase tracking-[0.36em] px-3 py-1.5" style={{
                background: "rgba(0,0,0,0.55)",
                color: "var(--text-2)",
                borderRadius: "999px",
                border: "1px solid rgba(255,255,255,0.1)",
                backdropFilter: "blur(8px)",
              }}>
                65 ft reach · Ground-level operation
              </div>
            </div>
          </div>

          {/* Copy right */}
          <div className="lg:col-span-5">
            <div className="font-mono text-[10px] uppercase tracking-[0.42em]" style={{ color: "var(--gold)" }}>
              How WFP works
            </div>
            <h3 className="mt-6 font-display" style={{ fontSize: "clamp(2rem, 3.2vw, 3rem)", letterSpacing: "-0.03em", lineHeight: 1.05 }}>
              Ultra-pure water — pushed up a carbon-fiber pole — cleans the glass by itself.
            </h3>
            <p className="mt-6 font-body" style={{ color: "var(--text-2)", fontSize: 17, lineHeight: 1.7 }}>
              Our five-stage filtration drops total dissolved solids to zero. The water actively pulls mineral residue off the glass, then dries perfectly clear — no soaps, no squeegees, no bucket rings. And we never leave the sidewalk.
            </p>

            <div className="mt-8 grid grid-cols-2 gap-4">
              {[
                { k: "0 TDS", v: "Trace-free result" },
                { k: "65 ft", v: "Max pole reach" },
                { k: "0 chem", v: "Municipal-safe runoff" },
                { k: "No ladder", v: "Zero fall risk" },
              ].map(({ k, v }, i) => (
                <div key={i} className="p-4" style={{
                  background: "rgba(255,255,255,0.03)",
                  border: "1px solid rgba(255,255,255,0.07)",
                  borderRadius: "14px",
                }}>
                  <div className="font-display text-2xl" style={{ letterSpacing: "-0.02em", color: "var(--gold-2)" }}>{k}</div>
                  <div className="mt-1 font-mono text-[10px] uppercase tracking-[0.32em]" style={{ color: "var(--text-3)" }}>{v}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
